import React, { useEffect, useRef } from 'react';
import {
  TouchableOpacity,
  Text,
  StyleProp,
  TextStyle,
  ViewStyle,
  ActivityIndicator,
  Animated,
} from 'react-native';
import { tw } from '../../../utils/tailwind';
import { twCb } from '../../../utils/helper';

const animationDuration = 1500;

interface FooterBarItemProps {
  active: boolean;
  text: string;
  loading: boolean;
  pulsating: boolean;
  buttonStyles?: StyleProp<ViewStyle>;
  disabled?: boolean;
  textStyles?: StyleProp<TextStyle>;
  loadingColor?: string;
  onPress: () => void;
  onPulsateComplete: () => void; // Callback to be triggered after each pulsate
}

export const FooterBarItem = (props: FooterBarItemProps) => {
  const colorAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const pulsate = Animated.loop(
      Animated.sequence([
        Animated.timing(colorAnim, {
          toValue: 1, // Transition to the second color
          duration: animationDuration,
          useNativeDriver: false, // Color transitions require `false`
        }),
        Animated.timing(colorAnim, {
          toValue: 0, // Back to the first color
          duration: animationDuration,
          useNativeDriver: false,
        }),
      ]),
    );

    if (props.pulsating) {
      pulsate.start();

      if (props.onPulsateComplete) {
        timeout = setInterval(() => {
          props.onPulsateComplete(); // Trigger callback after each pulsate cycle
        }, animationDuration); // Total duration of one animation loop
      }
    }

    return () => {
      pulsate.stop();

      if (timeout) {
        clearInterval(timeout);
      }
    };
  }, [props.pulsating, props.onPulsateComplete, colorAnim]);

  const getBackgroundColor = () => {
    if (props.pulsating) {
      return colorAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [tw.color('bg-success')!, tw.color('bg-gray-800')!], // From primary to a brighter color
      });
    } else {
      return tw.color(props.active ? 'bg-success' : 'bg-gray-800');
    }
  };

  return (
    <TouchableOpacity
      onPress={() => props.onPress()}
      activeOpacity={1}
      disabled={props.disabled}
      style={tw`md:basis-[19%] basis-[32%] grow justify-center`}>
      <Animated.View
        style={[
          tw`${twCb(
            'items-center px-3 py-6 grow',
            props.disabled ? 'opacity-80' : '',
          )}`,
          { backgroundColor: getBackgroundColor() },
          props.buttonStyles,
        ]}>
        {/* Store Area Name */}
        {props.loading ? (
          <ActivityIndicator
            size="small"
            color={props.loadingColor || '#fff'}
          />
        ) : (
          <Text
            style={[
              tw`text-lg leading-none font-medium text-white text-center`,
              props.textStyles,
            ]}>
            {props.text}
          </Text>
        )}
      </Animated.View>
    </TouchableOpacity>
  );
};

FooterBarItem.defaultProps = {
  loading: false,
  pulsating: false,
  onPulsateComplete: () => {}, // Default no-op callback
};
